
import {Configuration, Engine, IConfigurator, Configurator, AssetProvider, AssetInfoTypes} from "threngine";
import Axios from "axios";
import { isMobile } from "react-device-detect";
import { Hotspot3DRenderPass } from "./Hotspot3DRenderPass";
import { EventController } from "./Event/EventController";
import { refreshRateReadyPromise } from "../../refreshRate";

export let modelSet = new Map();
export let materialSet = new Map();
export let materialGroup = [];
export let states = [];
export let backgrounds = [];
export let visibilitySet = [];
export let renderSettings = [];

export interface IRenderEngine {
  settings: any,
  configurator: IConfigurator,

  initEngine(mount: HTMLDivElement): Promise<any>,
}

export class RenderEngine implements IRenderEngine {
  private _sceneConfig: Configuration;
  private _config: any;
  private _engine: Engine;
  private _assetProvider: AssetProvider;
  private _configurator: IConfigurator;
  private _hotspotRenderPass:Hotspot3DRenderPass;
  private _frameCounter:number = 0;
  private _frameDivider:number = 1;
  private _frameLimitBypass:boolean = false;
  private _effectiveFrameCount:number = 0;
  private _interval:any = -1;

  get settings() { return this._engine.services.render.settings };
  get assetProvider():AssetProvider { return this._assetProvider; }
  get configurator(): IConfigurator { return this._configurator; }
  get engine(){return this._engine}
  get scene(){return this._engine.services.render.scene}
  get camera(){return this._engine.services.render.camera}
  get outlinePass(){return this._engine.services.render.renderController.composer.outlinepass}
  get onBeforeRender(){return this._engine.services.render.onBeforeRender;}

  public get hotspotRenderPass():Hotspot3DRenderPass { return this._hotspotRenderPass; }

  initEngine = (): Promise<any> => {
    try {
      return Axios.get("config/appConfig.json").then(res => {
        this._config = res.data;
        return this._intialize();
      })
    } catch (e) {
      this._config = {
        mode: "dev",
        project_id: "62a0536dfe6e07001221a5d1",
        server: "https://tv1opft57b.execute-api.eu-central-1.amazonaws.com"
      };
      return this._intialize();
    }

  }

  getMaterialImagePath = (id:string) => {
    // HACK: Directly access image files through internal variable
    const path = (this._assetProvider as any)._assetUrl;
    
    if (!path) {
      console.warn("Could not retrieve material image.")
      return "";
    }

    return `${path}/${id}/preview.png`;
  }

  get release(){return this._config.endpoint}

  private _intialize = async (): Promise<any> => {
    const mount = document.getElementById("scene-canvas") as HTMLDivElement;

    this._assetProvider = new AssetProvider(this._config);

    this._engine = new Engine(this._assetProvider);
    return this._engine.initialize(mount)
    .then(() => {
      this._patchTAA();
      this._installHotspot3DRenderPass();
      this._installFrameDividerPatch();

      const sceneRenderer = (<any>this._engine.services.render.renderController.composer).sceneRenderer;

      // temporary workaround for iOS devices with Apple GPU:
      // assumes that we don't use MSAA
      if (sceneRenderer) {
        if (sceneRenderer.createNonMSAASceneRenderBuffer) {
          const patch = () => {
            sceneRenderer.nonMSAASceneRenderBuffer = sceneRenderer.sceneRenderBuffer;
          };

          sceneRenderer.createNonMSAASceneRenderBuffer = patch;
          patch();
        }
      }
    });
  }

  private _installFrameDividerPatch():void {
    const renderService = this._engine.services.render;

    const renderScene = renderService.renderScene;
    renderService.renderScene = (uptime:number) => {
      if ((this._frameCounter++ % this._frameDivider) == 0 || this._frameLimitBypass) {
        this._effectiveFrameCount++;
        renderScene(uptime);
      }
    };

    // TODO: clear interval eventually?
    setInterval(() => {
      const adaptiveQualityController = <any>this._engine.services.render.adaptiveQualityController;
      const averageFrameTime:number = adaptiveQualityController._averageFrameTime;
      const averageHistory:number[] = adaptiveQualityController._averageHistory;

      const lo = 1000/35;
      const hi = 1000/30;

      if (averageHistory.length == 10) {
        if (!this._frameLimitBypass && averageFrameTime > hi) {
          const stable = averageHistory.map(x => x > hi).reduce((a, b) => a && b, true);

          if (stable) {
            console.log("bypass on");
            this._frameLimitBypass = true;
          }
        }

        if (this._frameLimitBypass && averageFrameTime <= lo) {
          const stable = averageHistory.map(x => x <= lo).reduce((a, b) => a && b, true);

          if (stable) {
            console.log("bypass off");
            this._frameLimitBypass = false;
          }
        }
      }
    }, 1000);
  }

  private async _startMeasuringFrameRate():Promise<void> {
    this.engine.services.render.adaptiveQualityController.lowFrameTimeMs = 1000 / 55;
    this.engine.services.render.adaptiveQualityController.highFrameTimeMs = 1000 / 40;
    this.engine.services.render.adaptiveQualityController.enabled = true;

    // Start on low resolution on high-dpi screens.
    if (window.devicePixelRatio >= 1.5) {
      this.engine.services.render.renderController.composer.sceneRenderer.forceUpsamplingScale = 0.6666;
    }

    this._frameDivider = 1;
    this._frameLimitBypass = false;

    const refresh_rate = await refreshRateReadyPromise;

    // target 60 fps by default
    if (refresh_rate >= 100) {
      // 120 Hz display (hopefully)
      this._frameDivider = 2;
    }

    this._effectiveFrameCount = 0;

    let frame_rates = [];

    if (this._interval != -1) {
      clearInterval(this._interval);
    }

    const onAvgFrameRateAvailable = () => {
      clearInterval(this._interval);
      this._interval = -1;

      // Sort samples in !descending! order
      frame_rates.sort((a, b) => b - a);

      let frame_rate_avg = 0;
      let i = 0;
      for (i = 0; i < 3 && i < frame_rates.length; i++) {
        frame_rate_avg += frame_rates[i];
      }
      frame_rate_avg /= i;

      if (frame_rate_avg <= 45) {
        // device likely won't ever reach 60 fps
        // try to target 30 fps then
        if (refresh_rate >= 110) {
          // 120 Hz display (hopefully)
          this._frameDivider = 4;
        } else if (refresh_rate >= 88) {
          // 90 Hz display (hopefully)
          this._frameDivider = 3;
        } else {
          // 60 Hz display (hopefully)
          this._frameDivider = 2;
        }

        // Set new limits for dynamic quality
        this.engine.services.render.adaptiveQualityController.highFrameTimeMs = 1000/25;
        this.engine.services.render.adaptiveQualityController.lowFrameTimeMs = 1000/45;
      }

      // Debug code
      // alert("refresh rate: " + refresh_rate);
      // alert(frame_rates);
      // alert(frame_rate_avg);
      // alert("divider: " + this._frameDivider);
    };

    this._interval = setInterval(() => {
      frame_rates.push(this._effectiveFrameCount);
      this._effectiveFrameCount = 0;

      if (frame_rates.length == 10) {
        onAvgFrameRateAvailable();
      }
    }, 1000);
  }
  
  private _patchTAA():void {
    const sceneRenderer = (this._engine.services.render.renderController.composer as any).sceneRenderer;
    const taaCoalesceMaterial = sceneRenderer.taaCoalesceMaterial as THREE.ShaderMaterial;

    // Reduce anti-flicker transition time at the cost of slightly more flickering.
    taaCoalesceMaterial.fragmentShader = taaCoalesceMaterial.fragmentShader.replace(
      "float weight",
      "float weight = 0.12; //"
    );
  }

  private _installHotspot3DRenderPass():void {
    this._hotspotRenderPass = new Hotspot3DRenderPass(this.engine.services.render.camera);
    this.engine.services.render.renderController.composer.addPass(this.hotspotRenderPass);
  }

  public loadConfigByName(
    name:string,
    eventController:EventController
  ):Promise<THREE.Scene> {
    const sceneAsset = this._assetProvider.getAssets().find(
      a => a.infoType === AssetInfoTypes.Scene &&
      a.info.name === name);
    if (!sceneAsset) {
      throw `Failed to locate scene asset: ${name}`;
    }

    this._sceneConfig = eventController.rewriteSceneConfig(sceneAsset.info.data, this.assetProvider);
      
    // HACK: adjust camera zoom speed on mobile devices.
    if (isMobile) {
      this.assetProvider.getAssets().filter(asset => asset.infoType === AssetInfoTypes.Camera).forEach(asset => {
        asset.info.data.speed_zoom = 0.15;
      })
    }

    this._configurator = new Configurator(this._sceneConfig, this._engine);
    eventController.configurator = this._configurator;

    return (this._configurator as Configurator).initialize().then((scene:THREE.Scene) => {
      this._engine.services.render.setScene(scene);
      this._parseConfigAndAssets();
      if(isMobile){
        this.changeRenderSettingMobile();
      }

      // Disable the undersampling (via SSAA) setup by the artists,
      // because are going to use fancy temporal upsampling!
      this.engine.services.render.settings.antialias.webgl1.supersample.enabled = false;
      this.engine.services.render.settings.antialias.webgl2.supersample.enabled = false;

      this._startMeasuringFrameRate(); 
      return Promise.resolve(scene);
    });
  }

  private _parseConfigAndAssets():void {
    // TODO: can we get rid of this method as a whole?
    // Not sure what the purpose of all the preprocessing really is.
    modelSet.clear();
    materialSet.clear();
    materialGroup.length = 0;
    states.length = 0;
    backgrounds.length = 0;
    visibilitySet.length = 0;
    renderSettings.length = 0;

    this._assetProvider.getAssets().forEach((e)=>{
      if(e.infoType === "MATERIAL"){
        materialSet.set(e.id, e.info.name);
      }
    });

    // console.log("materialset", materialSet)

    this._configurator.config.visibility_groups.forEach((e)=>{
        modelSet.set(e.id, e.name);
    });

    // console.log("modelset", modelSet)

    this._configurator.config.material_groups.forEach(group => {
      let mats = group.material_ids.map((e)=>{
        return {
          id: e,
          name: materialSet.get(e)
        }
      });

      let current = this._configurator.state.material_groups.find(e => e.id === group.id);
      if (current) {
        mats.forEach((m, i)=>{
          if(m.id === current.material_id){
            let temp = mats[0];
            mats[0] = m;
            mats[i] = temp;
            return;
          }
        })
      }

      materialGroup.push({"id":group.id, "name":group.name, "mats":mats})
    });

    const backgroundAssets = this._assetProvider.getAssets().filter(a => a.infoType == AssetInfoTypes.Background); 
    backgroundAssets.forEach(asset => {
      backgrounds.push({
        id: asset.id,
        name: asset.info.name
      });
    });

    this._configurator.config.visibility_sets.forEach((e)=>{
      let group = e.visibility_group_ids;
      let current = this._configurator.state.visibility_sets.find(e => e.id === e.id);
        group.forEach((m, i)=>{
          if(m === current.visibility_group_id){
            let temp = group[0];
            group[0] = m;
            group[i] = temp;
            return;
          }
        })

      visibilitySet.push({"id":e.id, "name":e.name, "group":e.visibility_group_ids});
    });

    // console.log("visibility sets", visibilitySet);

    this._assetProvider.getAssets().filter(asset => asset.infoType === AssetInfoTypes.Settings).forEach(asset => {
      renderSettings.push({
        id: asset.id,
        name: asset.info.name}
      );
    });

    if(this.configurator.config.transforms){
      states = this.configurator.config.transforms.map(e => {
        return {
          id: e.id,
          name: e.name,
          state: e.state
        }
      })
    }

    // console.log("state", states)
  }

  public changeRenderSettingMobile():void {
    this.changeRenderSetting("id_0.09543252901210941");
  }

  public resize(){
    (this._engine as any)._onResize();
  }

  public changeRenderSetting(id:string){
    const renderSetting = this.assetProvider.getAssets()
      .find(asset => asset.infoType === AssetInfoTypes.Settings && asset.id === id);

    if (renderSetting) {
      this._engine.services.render.settings.setSettings(renderSetting.info.data);
    } else {
      throw `Cannot find render setting: ${id}`;
    }
  }

  private materialInfo=[];

  getMaterialImage = (id: string) => {
    const img = this.materialInfo.find(e => e.materialId === id);

    if (img) {
      return img.imageUrl;
    }

    const promise = this._assetProvider.getFile(id, "preview.png", "blob").then(data => {
      const b = new Blob([data], { type: "image/png" });
      return URL.createObjectURL(b);
    }).catch(()=>{
      return null;
    })

    return promise;
  }

  // getCamera(name:string):string{
  //   return this._configurator.config.cameras.find(e=>e.name===name).id;
  // }
}